// SP-1 Task 6 — single source of truth for calendar block status colors.
//
// Historically three separate components each hardcoded their own
// status→color map (Scheduling/parts/ApptBlock.tsx, Appointments/
// AppointmentDetailModal.tsx, and the legacy Appointments/TechScheduleView.tsx
// legend). This module replaces the first two; TechScheduleView keeps its own
// Ant-preset-name map (see note there) since it uses named Tag colors
// ('blue'/'green'/…) rather than hex values and isn't part of the redesigned
// Scheduling tab.
//
// `jobStatusColor` prefers the linked work order's stage (`work_order_status`,
// set once an appointment has a WO — Task 5) over the appointment's own
// `status`, because the WO stage is the more meaningful signal once work has
// actually started. This is the single seam a later task (SP-2) will make
// shop-editable — for now the palette below is the hardcoded default.
export interface StatusColorEntry {
  color: string;
  label: string;
}

const DEFAULT_ENTRY: StatusColorEntry = { color: '#8c8c8c', label: 'Unknown' };

// Covers both WO-stage values (new|created|scheduled|in_progress|
// waiting_parts|waiting_approval|completed|cancelled) and appointment-status
// values (scheduled|confirmed|in_progress|completed|cancelled|no_show) in one
// palette, since a handful of keys (scheduled/in_progress/completed/
// cancelled) are shared between the two vocabularies.
const PALETTE: Record<string, StatusColorEntry> = {
  new: { color: '#8c8c8c', label: 'New' },
  created: { color: '#8c8c8c', label: 'Created' },
  scheduled: { color: '#faad14', label: 'Scheduled' },
  confirmed: { color: '#13c2c2', label: 'Confirmed' },
  in_progress: { color: '#52c41a', label: 'In Progress' },
  waiting_parts: { color: '#fa8c16', label: 'Waiting on Parts' },
  waiting_approval: { color: '#722ed1', label: 'Waiting Approval' },
  completed: { color: '#1890ff', label: 'Completed' },
  cancelled: { color: '#ff4d4f', label: 'Cancelled' },
  no_show: { color: '#8c8c8c', label: 'No Show' },
};

export interface JobStatusInput {
  work_order_status?: string | null;
  status?: string;
}

/**
 * Resolve the fill color + label for a calendar block/status Tag. Prefers
 * the linked work order's stage when present, falling back to the
 * appointment's own status, and finally a neutral grey for anything
 * unrecognized.
 */
export function jobStatusColor(job: JobStatusInput): StatusColorEntry {
  const key = job.work_order_status || job.status;
  if (!key) return DEFAULT_ENTRY;
  return PALETTE[key] || DEFAULT_ENTRY;
}

// SP-1 review fix: several palette fills (scheduled amber, confirmed cyan)
// are light/bright enough that hardcoded white text fails contrast — hard to
// read on a phone in daylight, the owner's primary use case. This picks dark
// or white text per fill using the standard perceived-luminance formula.
// Threshold (0.55) is tuned so both cited light fills resolve to dark text
// while the genuinely dark fills (completed blue, cancelled red, etc.) keep
// white text.
export function readableTextColor(bgHex: string): '#ffffff' | '#1f2937' {
  const hex = bgHex.replace('#', '');
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.55 ? '#1f2937' : '#ffffff';
}
